import { COMPONENT_FIELD_SPEC } from "./constants";
import { regenerateNestedIds } from "./regenerate-nested-ids";
import { makeNavLabelUnique } from "./utils";
import type { PageComponent } from "../_types";

/**
 * Builds a duplicate of `source` — fresh id, `" (Copy)"`-suffixed title, every nested object-array
 * item's id regenerated (via {@link regenerateNestedIds}, spec-derived so a new nested array field
 * is covered automatically), and its navigation label de-duplicated against `allComponents` if
 * needed (via {@link makeNavLabelUnique}). Doesn't insert the clone anywhere — callers own
 * deciding where in their own components array it goes: `builder-store.ts`'s live store action
 * splices at `sourceIndex + 1` against live state, and the copilot's `apply-queued-change.ts` does
 * the same against its own folded working array, so this helper only builds the clone itself.
 * @param source - The component being duplicated — not mutated.
 * @param allComponents - Every component currently on the page, for nav-label de-duplication.
 */
export function buildDuplicatedComponent(source: PageComponent, allComponents: PageComponent[]): PageComponent {
  const clone = structuredClone(source);
  clone.id = crypto.randomUUID();
  clone.title = `${source.title} (Copy)`;

  regenerateNestedIds(clone as unknown as Record<string, unknown>, COMPONENT_FIELD_SPEC[clone.type]);

  if (clone.navigationLabel) {
    clone.navigationLabel = makeNavLabelUnique(clone.navigationLabel, allComponents);
  }

  return clone;
}
